import { get, isEqual } from "lodash";
import { useMemo, useRef, useCallback } from "react";

import useRootGroupsQuery from "src/hooks/useRootGroupsQuery";
import {
  buildTreeStructure,
  updateTreeObject,
} from "src/shared/TreeViewNext/treeUtils";

import {
  filterOutNonVisibleGroups,
  convertSortTeamsByGroupTypeFlag,
} from "src/components/GroupsNavigation/index.util";

/**
 * @param {Object} params
 * @param {*} [params.workspace]
 * @param {*} [params.featureFlags]
 * @param {*} [params.isSelected]
 * @param {*} [params.targetGroupTypes]
 * @param {*} [params.additionalVariables]
 * @param {*} [params.createTreeNodeImpl]
 * @param {*} [params.convertChildrenToArrayImpl]
 * @param {*} [params.addGroupsToRootsImpl]
 * @param {*} [params.query] Root query override forwarded to useRootGroupsQuery.
 * @param {*} [params.variables] Query variables override.
 * @param {boolean} [params.skip] useQuery skip override.
 * @param {boolean} [params.throwOnError] Whether to rethrow query errors.
 */
const useGroupsTreeData = ({
  workspace,
  featureFlags,
  isSelected,
  targetGroupTypes,
  additionalVariables,
  createTreeNodeImpl,
  convertChildrenToArrayImpl,
  addGroupsToRootsImpl,
  // Optional query overrides forwarded to useRootGroupsQuery so other trees can
  // reuse the same cache-churn/dedupe guards with a different root query.
  query,
  variables,
  skip,
  throwOnError,
}) => {
  const { data, loading: rootGroupsLoading } = useRootGroupsQuery({
    workspace,
    featureFlags,
    isSelected,
    additionalVariables,
    query,
    variables,
    skip,
    throwOnError,
  });

  const prevResult = useRef(null);

  const groupTypes = get(workspace, "config.groupTypes");

  const sortTeamsByGroupType = get(
    workspace,
    "config.featureFlags.sortTeamsByGroupType"
  );
  const sortOption = convertSortTeamsByGroupTypeFlag(sortTeamsByGroupType);

  const { root, treeData } = useMemo(() => {
    const filteredRootGroups = filterOutNonVisibleGroups(
      get(data, "pagedGroups.groups")
    );

    // Found an issue, the data returned from useRootGroupsQuery is changing.
    // Maybe because of some kind of cache updates... I couldn't find out why.
    // So add a check here to make sure we don't build the tree again,
    // if the groups used to build the tree are the same
    if (
      prevResult.current &&
      isEqual(filteredRootGroups, prevResult.current.groups)
    ) {
      return prevResult.current;
    }

    const result = buildTreeStructure(
      filteredRootGroups,
      groupTypes,
      undefined,
      sortOption,
      targetGroupTypes,
      createTreeNodeImpl,
      convertChildrenToArrayImpl,
      addGroupsToRootsImpl
    );
    prevResult.current = result;

    return result;
  }, [
    addGroupsToRootsImpl,
    convertChildrenToArrayImpl,
    createTreeNodeImpl,
    data,
    groupTypes,
    sortOption,
    targetGroupTypes,
  ]);

  const refreshTree = useCallback(() => {
    prevResult.current = updateTreeObject(treeData);
  }, [treeData]);

  return {
    root,
    treeData,
    rootGroupsLoading,
    groupTypes,
    refreshTree,
    sortOption,
  };
};

export default useGroupsTreeData;
